/**
 * Apple (RevenueCat) free-trial state, read client-side on iOS.
 *
 * Apple signups never get a WooCommerce subscription, so `user_subscription_state`
 * has no row for them; the RevenueCat entitlement is the source of truth there.
 * The membership entitlement carries `periodType` ("TRIAL" while in the intro
 * trial) and `expirationDate` (= trial end while on trial). Off iOS, or in a
 * build without the purchases module, this resolves immediately to "no trial".
 */
import {
  addCustomerInfoListener,
  getActiveMembershipEntitlement,
  getCustomerInfo,
  isPurchasesAvailable,
} from "@/lib/purchases/RevenueCat";
import type { CustomerInfo } from "react-native-purchases";
import { useEffect, useState } from "react";

export type AppleTrialState = {
  /** False until the first CustomerInfo read has settled (iOS only). */
  resolved: boolean;
  onTrial: boolean;
  trialEndAt: string | null;
};

export const NO_APPLE_TRIAL: AppleTrialState = {
  resolved: true,
  onTrial: false,
  trialEndAt: null,
};

export function appleTrialFromCustomerInfo(
  info: CustomerInfo | null,
  now: number = Date.now(),
): Pick<AppleTrialState, "onTrial" | "trialEndAt"> {
  const ent = getActiveMembershipEntitlement(info);
  if (!ent) return { onTrial: false, trialEndAt: null };
  const isTrialPeriod = String(ent.periodType ?? "").toUpperCase() === "TRIAL";
  const end = ent.expirationDate ? Date.parse(ent.expirationDate) : NaN;
  const trialEndAt = Number.isFinite(end) ? new Date(end).toISOString() : null;
  return {
    onTrial: isTrialPeriod && Number.isFinite(end) && end > now,
    trialEndAt: isTrialPeriod ? trialEndAt : null,
  };
}

/** Current Apple trial state for the signed-in user; re-reads on RevenueCat updates. */
export function useAppleTrial(userId: string | undefined): AppleTrialState {
  const available = isPurchasesAvailable();
  const [state, setState] = useState<AppleTrialState>(() =>
    available && userId ? { resolved: false, onTrial: false, trialEndAt: null } : NO_APPLE_TRIAL,
  );

  useEffect(() => {
    if (!available || !userId) {
      setState(NO_APPLE_TRIAL);
      return;
    }
    let cancelled = false;
    const apply = (info: CustomerInfo | null) => {
      if (cancelled) return;
      setState({ resolved: true, ...appleTrialFromCustomerInfo(info) });
    };
    void getCustomerInfo().then(apply);
    const unsubscribe = addCustomerInfoListener(apply);
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [available, userId]);

  return state;
}
